import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { Button } from '../../global/Button/Button';
// components
import { FormattedMessage } from 'dibs-react-intl';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';

// styles
import styles from './PdpSharedDescription.scss';

// helpers
import classNames from 'classnames';

const showOriginalText = (
    <FormattedMessage
        id="PdpSharedDescriptionAbout.seeOriginalHeaderText"
        defaultMessage="This item description was originally written in English."
    />
);

const showTranslatedText = (
    <FormattedMessage
        id="PdpSharedDescriptionAbout.seeTranslatedHeaderText"
        defaultMessage="This description is machine-translated and may contain inaccuracies."
    />
);

const showOriginalButton = (
    <FormattedMessage
        id="PdpSharedDescriptionAbout.seeDescriptionTextButton"
        defaultMessage="See description in German"
    />
);

const showTranslatedButton = (
    <FormattedMessage
        id="PdpSharedDescriptionAbout.seeOriginalTextButton"
        defaultMessage="See original description"
    />
);

export const Title = () => (
    <FormattedMessage id="PdpSharedDescriptionAbout.aboutHeader" defaultMessage="About" />
);

export const Content = ({ description, englishDescription, contentClass, collapsedHeight }) => {
    const [showOriginal, setShowOriginal] = useState(true);
    if (!description) {
        return null;
    }
    const enableTranslation = description !== englishDescription;
    const buttonText = showOriginal ? showOriginalButton : showTranslatedButton;
    const buttonHeader = showOriginal ? showOriginalText : showTranslatedText;
    const descriptionToShow = showOriginal ? englishDescription : description;

    return (
        <Fragment>
            {enableTranslation && (
                <div className={styles.content}>
                    {buttonHeader}
                    <div className={styles.buttonMargin}>
                        <Button
                            size="small"
                            isSecondary
                            dataTn="pdp-item-description-translations"
                            onClick={() => setShowOriginal(!showOriginal)}
                        >
                            {buttonText}
                        </Button>
                    </div>
                </div>
            )}
            <ExpandingAreaWithReadMore
                collapsedHeight={collapsedHeight || 192}
                showGradient={false}
            >
                <span
                    className={classNames(styles.content, contentClass)}
                    data-tn="pdp-item-description-content"
                    dangerouslySetInnerHTML={{ __html: (descriptionToShow || '').trim() }}
                />
            </ExpandingAreaWithReadMore>
        </Fragment>
    );
};

Content.propTypes = {
    description: PropTypes.string,
    englishDescription: PropTypes.string,
    contentClass: PropTypes.string,
    collapsedHeight: PropTypes.number,
};

const PdpSharedDescriptionAboutComponent = ({
    item,
    contentClass,
    headerClass,
    collapsedHeight,
}) => {
    if (!item || !item.description) {
        return null;
    }

    return (
        <div data-tn="pdp-item-description" className={styles.wrapper}>
            <h2
                className={classNames(styles.header, headerClass)}
                data-tn="pdp-item-description-title"
            >
                <Title />
            </h2>
            <Content
                description={item.description}
                englishDescription={item.englishDescription}
                contentClass={contentClass}
                collapsedHeight={collapsedHeight}
            />
        </div>
    );
};

PdpSharedDescriptionAboutComponent.propTypes = {
    contentClass: PropTypes.string,
    headerClass: PropTypes.string,
    item: PropTypes.object.isRequired,
    collapsedHeight: PropTypes.number,
};

export const PdpSharedDescriptionAbout = createFragmentContainer(
    PdpSharedDescriptionAboutComponent,
    {
        item: graphql`
            fragment PdpSharedDescriptionAbout_item on Item {
                description
                englishDescription
            }
        `,
    }
);
